import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { NIVELES } from "@/lib/api";

const emptyAlumno = {
  nombre: "", apellidos: "", fecha_nacimiento: "", nivel: "naranja",
  padre_id: "", sede_id: "", clase_ids: [],
  observaciones: "",
  gdpr_aceptado: false, gdpr_fecha: "", gdpr_imagen: false,
  tipo_suscripcion: "mensual", forma_pago: "stripe", importe: 0,
  estado: "activo", estado_pago: "pendiente",
  fecha_alta: new Date().toISOString().slice(0, 10),
  seguro_reposicion: false, clases_disponibles: 0, horarios_elegidos: [],
  plan_id: "", plan_nombre: "",
};

const emptyTutor = {
  nombre: "", apellidos: "", email: "", telefono: "", nif: "",
  direccion: "", ciudad: "", codigo_postal: "",
};

export const AlumnoDialog = ({ open, onOpenChange, sedes, clases, padres = [], planes = [], onSave, initial }) => {
  const [form, setForm] = useState(emptyAlumno);
  const [tutorNuevo, setTutorNuevo] = useState(false);
  const [tutor, setTutor] = useState(emptyTutor);

  useEffect(() => {
    if (open) {
      setForm({
        ...emptyAlumno, ...(initial || {}),
        sede_id: initial?.sede_id || "", padre_id: initial?.padre_id || "",
        plan_id: initial?.plan_id || "",
        clase_ids: initial?.clase_ids || [],
        horarios_elegidos: initial?.horarios_elegidos || [],
      });
      setTutorNuevo(false);
      setTutor(emptyTutor);
    }
  }, [open, initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setT = (k, v) => setTutor((t) => ({ ...t, [k]: v }));
  const planesSede = planes.filter((p) => !form.sede_id || p.sede_id === form.sede_id);

  const elegirPlan = (planId) => {
    const plan = planes.find((p) => p.id === planId);
    if (!plan) return;
    setForm((f) => ({
      ...f, plan_id: plan.id, plan_nombre: plan.nombre,
      importe: plan.importe,
      tipo_suscripcion: ["mensual", "trimestral", "semestral", "anual"].includes(plan.tipo) ? plan.tipo : f.tipo_suscripcion,
      seguro_reposicion: plan.activa_seguro || plan.seguro || f.seguro_reposicion,
      clases_disponibles: plan.clases_incluidas || f.clases_disponibles,
    }));
  };
  const toggleHorario = (clase, h) => {
    const puesto = form.horarios_elegidos.some(
      (x) => x.clase_id === clase.id && x.dia === h.dia && x.hora_inicio === h.hora_inicio);
    const horarios = puesto
      ? form.horarios_elegidos.filter(
        (x) => !(x.clase_id === clase.id && x.dia === h.dia && x.hora_inicio === h.hora_inicio))
      : [...form.horarios_elegidos, {
        clase_id: clase.id, clase: clase.nombre, dia: h.dia,
        hora_inicio: h.hora_inicio, hora_fin: h.hora_fin,
      }];
    setForm((f) => ({
      ...f, horarios_elegidos: horarios,
      clase_ids: [...new Set(horarios.map((x) => x.clase_id))],
    }));
  };

  const submit = () =>
    onSave({
      ...form,
      sede_id: form.sede_id || null,
      padre_id: tutorNuevo ? null : form.padre_id || null,
      padre_nuevo: tutorNuevo && tutor.nombre && tutor.email ? tutor : null,
      plan_id: form.plan_id || null,
      importe: Number(form.importe) || 0,
      clases_disponibles: Number(form.clases_disponibles) || 0,
      gdpr_fecha: form.gdpr_aceptado ? form.gdpr_fecha || new Date().toISOString().slice(0, 10) : "",
    });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="alumno-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{initial ? "Editar alumno" : "Nuevo alumno"}</DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="datos">
          <TabsList className="grid grid-cols-3 rounded-lg">
            <TabsTrigger value="datos" data-testid="tab-datos">Alumno</TabsTrigger>
            <TabsTrigger value="salud" data-testid="tab-salud">Consentimiento</TabsTrigger>
            <TabsTrigger value="pago" data-testid="tab-pago">Suscripción</TabsTrigger>
          </TabsList>

          <TabsContent value="datos" className="grid gap-4 pt-5">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Nombre</Label>
                <Input value={form.nombre} onChange={(e) => set("nombre", e.target.value)} data-testid="alumno-nombre-input" />
              </div>
              <div className="grid gap-2">
                <Label>Apellidos</Label>
                <Input value={form.apellidos} onChange={(e) => set("apellidos", e.target.value)} data-testid="alumno-apellidos-input" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Fecha de nacimiento</Label>
                <Input type="date" value={form.fecha_nacimiento} onChange={(e) => set("fecha_nacimiento", e.target.value)} data-testid="alumno-nacimiento-input" />
              </div>
              <div className="grid gap-2">
                <Label>Alumno desde</Label>
                <Input type="date" value={form.fecha_alta} onChange={(e) => set("fecha_alta", e.target.value)}
                  data-testid="alumno-fechaalta-input" />
              </div>
              <div className="grid gap-2">
                <Label>Nivel</Label>
                <Select value={form.nivel} onValueChange={(v) => set("nivel", v)}>
                  <SelectTrigger data-testid="alumno-nivel-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {NIVELES.map((n) => (
                      <SelectItem key={n.key} value={n.key}>
                        <span className="flex items-center gap-2">
                          <span className={`h-2 w-2 rounded-full ${n.dot}`} /> {n.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid gap-2">
              <div className="flex items-center justify-between gap-3">
                <Label>Padre / madre / tutor</Label>
                <button type="button" onClick={() => setTutorNuevo((v) => !v)}
                  data-testid="toggle-tutor-nuevo"
                  className="text-xs font-semibold text-primary hover:underline">
                  {tutorNuevo ? "Elegir uno existente" : "Registrar tutor nuevo aquí"}
                </button>
              </div>
              {!tutorNuevo ? (
                <>
                  <Select value={form.padre_id} onValueChange={(v) => set("padre_id", v)}>
                    <SelectTrigger data-testid="alumno-padre-select"><SelectValue placeholder="Selecciona un padre/tutor" /></SelectTrigger>
                    <SelectContent>
                      {padres.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.nombre} {p.apellidos} · {p.email}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Los datos de contacto, NIF/CIF y dirección se guardan en la ficha del padre/tutor.
                  </p>
                </>
              ) : (
                <div className="rounded-xl border border-primary/40 bg-accent/30 p-4 grid gap-4"
                  data-testid="tutor-nuevo-form">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label>Nombre del tutor</Label>
                      <Input value={tutor.nombre} onChange={(e) => setT("nombre", e.target.value)}
                        data-testid="tutor-nombre-input" />
                    </div>
                    <div className="grid gap-2">
                      <Label>Apellidos</Label>
                      <Input value={tutor.apellidos} onChange={(e) => setT("apellidos", e.target.value)}
                        data-testid="tutor-apellidos-input" />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label>Email</Label>
                      <Input type="email" value={tutor.email} onChange={(e) => setT("email", e.target.value)}
                        data-testid="tutor-email-input" />
                    </div>
                    <div className="grid gap-2">
                      <Label>Teléfono</Label>
                      <Input value={tutor.telefono} onChange={(e) => setT("telefono", e.target.value)}
                        data-testid="tutor-telefono-input" />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label>NIF / CIF</Label>
                      <Input value={tutor.nif} onChange={(e) => setT("nif", e.target.value)}
                        data-testid="tutor-nif-input" />
                    </div>
                    <div className="grid gap-2">
                      <Label>Dirección</Label>
                      <Input value={tutor.direccion} onChange={(e) => setT("direccion", e.target.value)}
                        data-testid="tutor-direccion-input" />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label>Ciudad</Label>
                      <Input value={tutor.ciudad} onChange={(e) => setT("ciudad", e.target.value)}
                        data-testid="tutor-ciudad-input" />
                    </div>
                    <div className="grid gap-2">
                      <Label>Código postal</Label>
                      <Input value={tutor.codigo_postal} onChange={(e) => setT("codigo_postal", e.target.value)}
                        data-testid="tutor-cp-input" />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Al guardar se crea el tutor y se le envía su enlace para completar el perfil,
                    crear su contraseña y aceptar los términos. Si el email ya existe, se reutiliza esa ficha.
                  </p>
                </div>
              )}
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Sede</Label>
                <Select value={form.sede_id} onValueChange={(v) => set("sede_id", v)}>
                  <SelectTrigger data-testid="alumno-sede-select"><SelectValue placeholder="Selecciona sede" /></SelectTrigger>
                  <SelectContent>
                    {sedes.map((s) => <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Estado</Label>
                <Select value={form.estado} onValueChange={(v) => set("estado", v)}>
                  <SelectTrigger data-testid="alumno-estado-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="activo">Activo</SelectItem>
                    <SelectItem value="pausado">Pausado</SelectItem>
                    <SelectItem value="baja">Baja</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid gap-2">
              <Label>Día y hora de clase</Label>
              <div className="space-y-3">
                {clases
                  .filter((c) => !form.sede_id || c.sede_id === form.sede_id)
                  .map((c) => (
                    <div key={c.id}>
                      <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">
                        {c.nombre}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {(c.horarios || []).map((h) => {
                          const puesto = form.horarios_elegidos.some(
                            (x) => x.clase_id === c.id && x.dia === h.dia && x.hora_inicio === h.hora_inicio);
                          return (
                            <button key={`${c.id}-${h.dia}-${h.hora_inicio}`} type="button"
                              onClick={() => toggleHorario(c, h)}
                              data-testid={`alumno-horario-${c.id}-${h.dia}-${h.hora_inicio}`}
                              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors duration-200 ${
                                puesto ? "bg-primary text-primary-foreground border-primary"
                                  : "border-border text-muted-foreground hover:bg-muted"}`}>
                              {h.dia} · {h.hora_inicio}–{h.hora_fin}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                {clases.length === 0 && <p className="text-sm text-muted-foreground">Crea clases primero.</p>}
              </div>
              <p className="text-xs text-muted-foreground">
                Al elegir un turno el alumno queda matriculado en esa clase y aparece en su calendario.
              </p>
            </div>
          </TabsContent>

          <TabsContent value="salud" className="grid gap-4 pt-5">
            <div className="grid gap-2">
              <Label>Observaciones</Label>
              <Textarea value={form.observaciones} onChange={(e) => set("observaciones", e.target.value)}
                placeholder="Notas internas sobre el alumno/a" data-testid="alumno-observaciones-input" />
            </div>
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3">
              <p className="text-sm font-semibold text-amber-700 dark:text-amber-400">
                El consentimiento lo da la familia, no el centro
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Al guardar, el tutor recibirá un email con la matrícula, los datos de la inscripción y
                un enlace para aceptar los términos y la privacidad él mismo (exigido por el RGPD).
                Hasta entonces la ficha queda como <b>pendiente de consentimiento</b>.
              </p>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <div>
                <Label>GDPR aceptado</Label>
                <p className="text-xs text-muted-foreground mt-1">Consentimiento firmado por el tutor</p>
              </div>
              <Switch checked={form.gdpr_aceptado} onCheckedChange={(v) => set("gdpr_aceptado", v)} data-testid="alumno-gdpr-switch" />
            </div>
            {form.gdpr_aceptado && (
              <div className="grid gap-2">
                <Label>Fecha de aceptación GDPR</Label>
                <Input type="date" value={form.gdpr_fecha} onChange={(e) => set("gdpr_fecha", e.target.value)} data-testid="alumno-gdpr-fecha-input" />
              </div>
            )}
            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <div>
                <Label>Cesión de derechos de imagen</Label>
                <p className="text-xs text-muted-foreground mt-1">Permite publicar fotos del alumno</p>
              </div>
              <Switch checked={form.gdpr_imagen} onCheckedChange={(v) => set("gdpr_imagen", v)} data-testid="alumno-gdpr-imagen-switch" />
            </div>
          </TabsContent>

          <TabsContent value="pago" className="grid gap-4 pt-5">
            <div className="grid gap-2">
              <Label>Plan de pago</Label>
              <Select value={form.plan_id} onValueChange={elegirPlan}>
                <SelectTrigger data-testid="alumno-plan-select">
                  <SelectValue placeholder={form.sede_id ? "Elige un plan de esta sede" : "Elige primero la sede"} />
                </SelectTrigger>
                <SelectContent>
                  {planesSede.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.nombre} · {p.importe} € {p.categoria ? `· ${p.categoria}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Al elegir un plan se rellenan la cuota, el importe y el seguro. Con forma de pago
                manual (efectivo, transferencia, bizum…) se genera además un <b>cobro pendiente</b>.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Tipo de suscripción</Label>
                <Select value={form.tipo_suscripcion} onValueChange={(v) => set("tipo_suscripcion", v)}>
                  <SelectTrigger data-testid="alumno-suscripcion-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mensual">Mensual</SelectItem>
                    <SelectItem value="trimestral">Trimestral</SelectItem>
                    <SelectItem value="semestral">Semestral</SelectItem>
                    <SelectItem value="anual">Anual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Forma de pago</Label>
                <Select value={form.forma_pago} onValueChange={(v) => set("forma_pago", v)}>
                  <SelectTrigger data-testid="alumno-formapago-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="stripe">Stripe (portal de familias)</SelectItem>
                    <SelectItem value="transferencia">Transferencia</SelectItem>
                    <SelectItem value="domiciliacion">Domiciliación bancaria</SelectItem>
                    <SelectItem value="tarjeta">Tarjeta en sede</SelectItem>
                    <SelectItem value="efectivo">Efectivo</SelectItem>
                    <SelectItem value="bizum">Bizum</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="grid gap-2">
                <Label>Importe (€)</Label>
                <Input type="number" value={form.importe} onChange={(e) => set("importe", e.target.value)} data-testid="alumno-importe-input" />
              </div>
              <div className="grid gap-2">
                <Label>Estado de pago</Label>
                <Select value={form.estado_pago} onValueChange={(v) => set("estado_pago", v)}>
                  <SelectTrigger data-testid="alumno-estadopago-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="al_dia">Al día</SelectItem>
                    <SelectItem value="pendiente">Pendiente</SelectItem>
                    <SelectItem value="impago">Impago</SelectItem>
                    <SelectItem value="cancelado">Cancelado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-primary/40 bg-accent/40 p-3">
              <div>
                <Label>Seguro de recuperación de clases</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Con seguro puede recuperar clases perdidas; sin seguro la clase se descuenta.
                </p>
              </div>
              <Switch checked={form.seguro_reposicion} onCheckedChange={(v) => set("seguro_reposicion", v)} data-testid="alumno-seguro-switch" />
            </div>
            <div className="grid gap-2">
              <Label>Clases disponibles en bono</Label>
              <Input type="number" value={form.clases_disponibles} onChange={(e) => set("clases_disponibles", e.target.value)} data-testid="alumno-clases-disponibles-input" />
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button className="rounded-full font-semibold" onClick={submit} data-testid="save-alumno-btn">Guardar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
